import React, { useRef, useState, useMemo } from 'react';
import Popup from '@atlaskit/popup';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { colors, Tooltip } from '@atlassian/jira-portfolio-3-common';
import type { DependencyLine } from '@atlassian/jira-portfolio-3-dependency-line-detail/src/common/types';
import DependencyModalContent from '@atlassian/jira-portfolio-3-dependency-line-detail/src/common/ui/content';
import { PRODUCT_ANALYTICS_EVENT_NAMES } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/analytics/types';
import { isDefined } from '@atlassian/jira-portfolio-3-portfolio/src/common/ramda';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import messages from './messages';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
import * as styles from './styles.module.css';
import type { Props, ClickArea } from './types';

export default function Connection({
	id,
	label,
	h,
	w,
	x,
	y,
	line,
	isRollup,
	links,
	highlight,
	onLinkClick,
	isDetailOpen,
	isNegativeLeadTime,
	issueMap,
	issueLinkTypesById,
	issueTypesById,
	projectsById,
	isEditMode,
	removeDependency,
	onDeleteLink,
}: Props) {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isHighlighted, setIsHighlighted] = useState(false);
	const [areaClicked, setAreaClicked] = useState<ClickArea>(undefined);
	const popupOffset = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
	const labelRef = useRef<HTMLDivElement | null>(null);
	const intl = useIntl();

	const resetState = () => {
		setAreaClicked(undefined);
		popupOffset.current = { x: 0, y: 0 };
	};

	const reportAnalytics = () => {
		const [actionSubject, action] =
			PRODUCT_ANALYTICS_EVENT_NAMES.CLICKED_DEPENDENCY_LINE.split(' ');
		fireUIAnalytics(createAnalyticsEvent({ action, actionSubject }), {
			associatedLinks: links.length,
		});
	};

	const fallbackPathColor =
		isHighlighted || highlight || isDetailOpen
			? token('color.text.brand', colors.B400)
			: token('color.text.subtle', colors.N70);

	const pathColor = isNegativeLeadTime
		? token('color.text.danger', colors.R400)
		: fallbackPathColor;

	const labelColor = isNegativeLeadTime
		? token('color.text.danger', colors.R400)
		: token('color.text', colors.N900);

	const dependencyLines = useMemo((): DependencyLine[] => {
		const lines: DependencyLine[] = [];
		links.forEach((link) => {
			const from = issueMap[link.sourceItemKey];
			const to = issueMap[link.targetItemKey];
			if (!isDefined(from) || !isDefined(to)) {
				return;
			}
			const fromProject = projectsById[from.project];
			const fromIssueType = issueTypesById[from.type];
			const toProject = projectsById[to.project];
			const toIssueType = issueTypesById[to.type];
			lines.push({
				itemKey: link.itemKey,
				issueLinkType: issueLinkTypesById[link.type],
				drawable: false,
				fromIssue: {
					id: from.id,
					title: from.summary,
					issueKey: from.issueKey,
					projectKey: fromProject?.key,
					iconUrl: fromIssueType?.iconUrl,
					typeName: fromIssueType?.name,
					startDate: from.baselineStart,
					dueDate: from.baselineEnd,
					indexOnScope: 0,
					isHidden: false,
				},
				toIssue: {
					id: to.id,
					title: to.summary,
					issueKey: to.issueKey,
					projectKey: toProject?.key,
					iconUrl: toIssueType?.iconUrl,
					typeName: toIssueType?.name,
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					startDate: to.baselineStart as number,
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					dueDate: to.baselineEnd as number,
					indexOnScope: 0,
					isHidden: false,
				},
			});
		});
		return lines;
	}, [links, issueMap, issueLinkTypesById, issueTypesById, projectsById]);

	const DependencyLinkFlyout = (
		<DependencyModalContent
			links={dependencyLines}
			canDelete={isEditMode}
			onUnlink={(payload) => {
				resetState();
				removeDependency(payload);
				onDeleteLink(payload.itemKey);
			}}
			isOverlapping={() => isNegativeLeadTime}
		/>
	);

	// eslint-disable-next-line @typescript-eslint/no-shadow
	const onClick = (areaClicked: ClickArea) => {
		// Set flag to undefined so Popup is hidden
		setAreaClicked(undefined);
		// We need to set timeout with 0ms to trigger the state change on `areaClicked`
		// This is so the Popup can re-render in the right position
		setTimeout(() => {
			setAreaClicked(areaClicked);
			onLinkClick(id, links.length);
			reportAnalytics();
		}, 0);
	};

	return (
		<>
			<foreignObject
				x={x}
				y={y}
				width={w}
				height={Math.max(h, 1)}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				style={{ overflow: 'visible', pointerEvents: 'none' }}
			>
				<div
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
					className={styles.connection}
					style={{
						width: w,
						height: h,
						color:
							isHighlighted || highlight
								? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
									token('color.text.brand', colors.B400)
								: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
									token('color.text.brand', colors.N70),
					}}
				>
					<Popup
						isOpen={isDetailOpen && areaClicked !== undefined}
						offset={
							areaClicked === 'path' ? [popupOffset.current.x, popupOffset.current.y] : [0, 0]
						}
						onClose={() => {
							resetState();
						}}
						// when connection label is clicked, we place the popup at the bottom of the label
						placement="bottom"
						shouldFlip={false}
						content={() => DependencyLinkFlyout}
						trigger={(triggerProps) => (
							<Tooltip content={intl.formatMessage(messages.viewDetails)}>
								<div
									{...triggerProps}
									// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
									className={styles.label}
									data-testid={`portfolio-3-portfolio.app-simple-plans.main.tabs.dependencies.connections.connection-${id.replace(
										':',
										'-',
									)}`}
									style={{
										// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
										color: labelColor,
									}}
									role="button"
									tabIndex={0}
									onMouseEnter={() => setIsHighlighted(true)}
									onMouseLeave={() => setIsHighlighted(false)}
									onClick={(e) => {
										onClick('label');
										e.stopPropagation();
									}}
									onKeyDown={() => {
										onClick('label');
									}}
								>
									<span ref={labelRef}>{label}</span>
								</div>
							</Tooltip>
						)}
					/>
				</div>
			</foreignObject>

			{/* The first path describes how the path looks */}
			<path
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				d={line as string | undefined}
				style={{
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					pointerEvents: 'visibleStroke',
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					fill: 'transparent',
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					stroke: pathColor,
					strokeWidth: isHighlighted || highlight || isDetailOpen ? '2px' : '1px',
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					cursor: 'pointer',
				}}
				strokeDasharray={isRollup ? '5,5' : ''}
				markerEnd={`url(#${id})`}
			/>
			{/* The second path is the clickable target area, with wider width so it's easier to click */}
			<Tooltip
				position="mouse"
				mousePosition="top"
				content={intl.formatMessage(messages.viewDetails)}
				tag="g"
				hideTooltipOnClick
			>
				<path
					onMouseEnter={() => setIsHighlighted(true)}
					onMouseLeave={() => setIsHighlighted(false)}
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					d={line as string | undefined}
					style={{
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
						pointerEvents: 'visibleStroke',
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
						fill: 'transparent',
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
						stroke: 'transparent',
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
						strokeWidth: '30px',
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
						cursor: 'pointer',
					}}
					onClick={(e) => {
						const labelPos = labelRef.current?.getBoundingClientRect();

						popupOffset.current = {
							x: e.clientX - (labelPos ? labelPos.x : 0),
							y: e.clientY - (labelPos ? labelPos.y : 0),
						};
						onClick('path');
						e.stopPropagation();
					}}
				/>
			</Tooltip>

			<defs>
				<marker id={id} markerWidth="33" markerHeight="33" refX="7" refY="6" orient="auto">
					<path
						d="M2,2 L2,11 L10,6 L2,2"
						style={{
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
							fill: pathColor,
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
							cursor: 'pointer',
						}}
					/>
				</marker>
			</defs>
		</>
	);
}
